import { Link } from 'react-router-dom';

/**
 * The one back affordance for every step of the enrollment journey.
 *
 * ── WHY A COMPONENT AND NOT FOUR `<Link>`s ─────────────────────────────────
 * Each funnel page had grown its own: `text-sm text-primary underline` in three
 * places, `text-sm font-semibold … hover:no-underline` in a fourth, an arrow on one
 * of them, and a label that said "Back" on one page and "Back to credit" on another.
 * A shopper stepping backwards through five screens met a control that moved and
 * changed shape each time. One component means adding a step cannot reintroduce that.
 *
 * ⚠️ THE ARROW IS DECORATIVE (`aria-hidden`). The link text already names the
 * destination, so announcing the glyph would just repeat it.
 *
 * ⚠️ THE UNDERLINE SITS ON THE TEXT ONLY. Underlining the whole inline-flex renders
 * as a stray tick beneath the arrow.
 *
 * ── ON `to`, AND WHY THERE IS NO `history.back()` HERE ─────────────────────
 * Callers pass an explicit route. `navigate(-1)` looks tempting and is wrong for this
 * funnel: a shopper can arrive at `…/credit` from the plan detail, from a rehydrated
 * refresh, or from a deep link, and "the previous history entry" is a different place
 * in each case — sometimes outside the app entirely. An explicit target always steps
 * one stage back up the funnel.
 */
/**
 * Either a route (`to`) or an in-page step back (`onBack`) — never both.
 *
 * ⚠️ `onBack` EXISTS BECAUSE NOT EVERY STEP IS A ROUTE. The credit page renders the
 * identity form and the credit decision on ONE url, swapping on `isSuccess`. From the
 * decision, "back" means the form — a state change, not a navigation — and a `<Link>`
 * there would jump the shopper two conceptual steps out of the page. Same control,
 * same appearance, so the affordance does not change shape mid-funnel.
 */
export type FunnelBackLinkProps =
  | {
      /** Explicit destination — the previous funnel step, never `-1`. */
      readonly to: string;
      readonly onBack?: never;
      /** Already translated. Should name the destination, not just say "Back". */
      readonly label: string;
    }
  | {
      readonly to?: never;
      /** Step back WITHIN the page. Must not itself re-trigger any costly work. */
      readonly onBack: () => void;
      readonly label: string;
    };

/* ICON-ONLY since the premium pass: the sentence-length label ("Back to
   start — choose a different meter") read as page furniture. The label is not
   dropped — it becomes the accessible name and the hover tooltip, so a screen
   reader still hears the destination and a mouse still gets told. */
const SHELL =
  'group inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-surface text-foreground/70 shadow-sm transition-colors hover:border-primary hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary';

function Arrow(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4 transition-transform group-hover:-translate-x-0.5"
    >
      <path d="M19 12H5" />
      <path d="m12 19-7-7 7-7" />
    </svg>
  );
}

export function FunnelBackLink(props: FunnelBackLinkProps): JSX.Element {
  // A state change is a BUTTON, not a link with a fake href — the distinction is what
  // tells a screen reader (and a middle-click) whether this navigates.
  if (props.to === undefined) {
    return (
      <button
        type="button"
        onClick={props.onBack}
        aria-label={props.label}
        title={props.label}
        className={SHELL}
      >
        <Arrow />
      </button>
    );
  }
  return (
    <Link to={props.to} aria-label={props.label} title={props.label} className={SHELL}>
      <Arrow />
    </Link>
  );
}
